/**
 * @file useRedirects.ts
 * @description Список «Сайт → SEO → Переадресации» (phase19.16, партия Р3в,
 * v0.19.64; SB131; db-design-r3v §4): поиск по адресу (с задержкой),
 * фильтр «Все · Авто · Ручные» и «Не действуют» (ссылка из «Проверки
 * сайта» — `?status=inactive`), постранично по 50 («Показать ещё»), сводка
 * для счётчиков и полосы предела 900/1000. После добавления, правки и
 * удаления список перечитывается с сервера (один источник правды).
 */

import { useCallback, useEffect, useRef, useState } from "react";

import { siteRedirects, type SiteRedirectFilter, type SiteRedirectItem, type SiteRedirectStatusFilter, type SiteRedirectSummary } from "../../lib/api-site-redirects";
import { seoErrorText } from "../../lib/site-seo/seo-errors";

const SEARCH_DELAY_MS = 300;

export interface RedirectsApi {
  items: SiteRedirectItem[] | null;
  summary: SiteRedirectSummary | null;
  loading: boolean;
  error: string | null;
  query: string;
  setQuery: (q: string) => void;
  filter: SiteRedirectFilter;
  setFilter: (f: SiteRedirectFilter) => void;
  /** Только не действующие. */
  inactiveOnly: boolean;
  setInactiveOnly: (value: boolean) => void;
  hasMore: boolean;
  loadingMore: boolean;
  loadMore: () => Promise<void>;
  reload: () => Promise<void>;
}

export function useRedirects(enabled = true, initialInactive = false): RedirectsApi {
  const [items, setItems] = useState<SiteRedirectItem[] | null>(null);
  const [summary, setSummary] = useState<SiteRedirectSummary | null>(null);
  const [cursor, setCursor] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [query, setQuery] = useState("");
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState<SiteRedirectFilter>("all");
  const [inactiveOnly, setInactiveOnly] = useState(initialInactive);
  const status: SiteRedirectStatusFilter = inactiveOnly ? "inactive" : "all";
  // Ответ на устаревший запрос (сменили фильтр, пока грузилось) не кладём.
  const request = useRef(0);

  useEffect(() => {
    const t = setTimeout(() => setSearch(query), SEARCH_DELAY_MS);
    return () => clearTimeout(t);
  }, [query]);

  const reload = useCallback(async () => {
    if (!enabled) return;
    const id = ++request.current;
    setLoading(true);
    setError(null);
    try {
      const page = await siteRedirects.list({ q: search, source: filter, status });
      if (id !== request.current) return;
      setItems(page.items);
      setSummary(page.summary);
      setCursor(page.nextCursor);
    } catch (err) {
      if (id === request.current) setError(seoErrorText(err, "Не удалось загрузить переадресации."));
    } finally {
      if (id === request.current) setLoading(false);
    }
  }, [enabled, filter, search, status]);

  useEffect(() => {
    void reload();
  }, [reload]);

  const loadMore = useCallback(async () => {
    if (!cursor) return;
    const id = request.current;
    setLoadingMore(true);
    try {
      const page = await siteRedirects.list({ q: search, source: filter, status, cursor });
      if (id !== request.current) return;
      setItems((prev) => [...(prev ?? []), ...page.items]);
      setSummary(page.summary);
      setCursor(page.nextCursor);
    } catch (err) {
      setError(seoErrorText(err, "Не удалось загрузить ещё."));
    } finally {
      setLoadingMore(false);
    }
  }, [cursor, filter, search, status]);

  return { items, summary, loading, error, query, setQuery, filter, setFilter, inactiveOnly, setInactiveOnly, hasMore: cursor !== null, loadingMore, loadMore, reload };
}
